import { Link } from '@on3/ui-lib/components/Link';
import { useAuth } from '@on3/ui-lib/index';
import { databaseUrl } from '@on3/ui-lib/utils/api';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import type { ICoachProfilePageProps } from 'pages/db/coach/[id]';

import { PlayerBio } from '../PlayerBio';
import { PlayerSearch } from '../PlayerSearch';
import { CoachAgentModule } from './CoachAgentModule';
import { CoachHistory } from './CoachHistory';
import { CoachInfo } from './CoachInfo';
import { CoachNewsModule } from './CoachNewsModule';
import styles from './CoachProfile.module.scss';
import { PositionInfo } from './PositionInfo';
import { ProfileQuote } from './ProfileQuote';

export const CoachProfile = ({
  coach,
  coachHistory,
  coachArticles,
  coachQuotes,
  coachAgent,
  player,
}: ICoachProfilePageProps) => {
  const isCoachAgent =
    !!coachAgent?.agent || !!coachAgent?.agency || !!coachAgent?.secondaryAgent;
  const { formatHref } = useDynamicRoute();

  const { hasRdbAccess } = useAuth();
  const { fullName, defaultAsset, slug, description } = coach;
  const showQuotes = !!coachQuotes?.list?.length;
  const showViewAll = coachHistory?.list && coachHistory?.list?.length > 8;
  const viewAllCoachHistoryRoute = `/db/coach/${coach?.slug}/coaching-history/`;

  return (
    <div className={styles.block}>
      {hasRdbAccess() && (
        <a
          href={`${databaseUrl}/people/${slug}/edit/`}
          rel="noopener noreferrer"
          target="_blank"
        >
          Edit
        </a>
      )}
      <header
        className={clsx(styles.header, {
          [styles.hasNavBar]: player?.isAthlete && player?.isCoach,
        })}
      >
        <PlayerSearch />
        <div className={styles.nameNavWrapper}>
          <h1>{fullName}</h1>
          {player?.isAthlete && player?.isCoach && (
            <nav className={styles.coachesNav}>
              <Link
                className={styles.tab}
                href={formatHref({
                  path: '/db/[id]/',
                })}
              >
                Player Profile
              </Link>
              <span className={clsx(styles.tab, styles.active)}>
                Coach Profile
              </span>
            </nav>
          )}
        </div>
      </header>

      <div className={styles.profileContainer}>
        <On3Avatar
          altText={defaultAsset?.altText}
          className={styles.playerAvatar}
          domain={defaultAsset?.domain}
          fit="cover"
          height={200}
          source={defaultAsset?.source}
          width={200}
        />
        <PositionInfo data={coach} />
        <CoachInfo data={coach} />
      </div>
      <div className={styles.breadcrumb}>
        <TextLink href="/db/">On3 DB</TextLink>
        <Text className={styles.name} component="span">
          {fullName}
        </Text>
      </div>
      <div className={styles.coachProfileContainer}>
        {isCoachAgent && <CoachAgentModule coachAgent={coachAgent} />}
        {description && <PlayerBio description={coach.description} />}
        {showQuotes && (
          <ProfileQuote list={coachQuotes?.list} slug={coach.slug} />
        )}
        <div className={styles.contentWrapper}>
          {!!coachArticles?.list?.length && (
            <CoachNewsModule coachArticles={coachArticles} />
          )}
          <div>
            {coachHistory && (
              <CoachHistory list={coachHistory?.list?.slice(0, 8)} />
            )}
            {showViewAll && (
              <footer className={styles.viewAll}>
                <TextLink href={viewAllCoachHistoryRoute}>View all</TextLink>
              </footer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
